import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { apiClient } from '@api';
import { webEnvConfig } from '@config';
import { container } from '@containers';
import {
  FeedbackRating,
  KomplyanceAiAssessmentCompletedChunk,
  KomplyanceAiAssessmentProgressChunk,
  KomplyanceAiAssessmentStartedChunk,
  KomplyanceAiChunk,
  KomplyanceAiChunkSchema,
  KomplyanceAiFileIngestProgressChunk,
  KomplyanceAiStepProgressChunk,
  KomplyanceAiWorkflowStartedChunk,
  LoginResponse,
  StopGenerationResponse,
} from '@libs/types';
import { AuthStore, StreamCallbacks, StreamController, StreamingStore } from '@stores';

import { DI_TOKENS } from '../../../constants';

import { takeCompleteEvents } from './streamFraming';

// Constants
const API_ENDPOINTS = {
  SEND_MESSAGE_STREAM: 'api/komplyance-ai/send-message-stream',
  SEND_MESSAGE_STREAM_DEEP: 'api/komplyance-ai/send-message-stream-deep',
  STOP_GENERATION: (taskId: string) => `api/komplyance-ai/chat-messages/${taskId}/stop`,
  SEND_FEEDBACK: (messageId: string) => `api/komplyance-ai/messages/${messageId}/feedback`,
} as const;

export interface StreamRequestOptions {
  /** Route to the Deep Research endpoint (enforces the Quality Rubric). */
  deepResearch?: boolean;
}

const resolveStreamEndpoint = (options?: StreamRequestOptions): string =>
  options?.deepResearch ? API_ENDPOINTS.SEND_MESSAGE_STREAM_DEEP : API_ENDPOINTS.SEND_MESSAGE_STREAM;

// Error handling
const handleApiError = (error: unknown): Error => {
  if (error && typeof error === 'object' && 'response' in error) {
    const axiosError = error as AxiosError<{
      error?: string;
      description?: string;
    }>;
    const message =
      axiosError.response?.data?.error || axiosError.response?.data?.description || 'Unknown error';
    return new Error(message);
  }
  if (error instanceof Error) return error;
  return new Error('An unexpected error occurred');
};

// Stream processing
// Note: StreamCallbacks and StreamController are defined in @stores/StreamingStore
// and exported from there to avoid circular dependencies

// Unused type re-exports kept for backwards compat
export type { KomplyanceAiAssessmentCompletedChunk as komplyanceAiAssessmentCompletedChunk, KomplyanceAiAssessmentProgressChunk as komplyanceAiAssessmentProgressChunk, KomplyanceAiAssessmentStartedChunk as komplyanceAiAssessmentStartedChunk, KomplyanceAiFileIngestProgressChunk as komplyanceAiFileIngestProgressChunk };

/**
 * Refresh the access token for streaming requests (mirrors apiClient interceptor logic).
 */
const refreshAccessTokenForStream = async (authStore: AuthStore): Promise<string | null> => {
  const refreshToken = authStore.refreshToken;
  if (!refreshToken) return null;

  try {
    const response = await fetch(`${webEnvConfig.api.baseURL}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) return null;

    const data: LoginResponse = await response.json();
    if (!data) return null;

    authStore.setToken(data.accessToken);
    authStore.setRefreshToken(data.refreshToken);
    authStore.setUserProfile(data.userInfo);

    return data.accessToken;
  } catch {
    return null;
  }
};

const parseStreamError = async (response: Response): Promise<string> => {
  try {
    const data = await response.json();
    return data.error || data.description || 'Stream failed';
  } catch {
    return `HTTP ${response.status}`;
  }
};

const processkomplyanceAiChunk = (chunk: KomplyanceAiChunk, callbacks?: StreamCallbacks): boolean => {
  try {
    if (chunk.event === 'message_end') {
      callbacks?.onDocuments?.(chunk);
      // Don't exit here — workflow_finished comes after and triggers onComplete/setIsStreaming(false)
    } else if (chunk.event === 'workflow_started') {
      callbacks?.onMetadata?.(chunk);
    } else if (chunk.event === 'message' && chunk.answer) {
      callbacks?.onChunk?.(chunk);
    } else if (chunk.event === 'error') {
      callbacks?.onError?.(chunk);
    } else if (chunk.event === 'workflow_finished') {
      callbacks?.onComplete?.(chunk);
      return true; // workflow_finished is the true end-of-stream signal
    } else if (chunk.event === 'node_started' || chunk.event === 'node_finished') {
      // Silently ignore workflow node events - these are internal komplyanceAi events
    } else if (chunk.event === 'file_ingest_progress') {
      callbacks?.onFileIngestProgress?.(chunk as KomplyanceAiFileIngestProgressChunk);
    } else if (chunk.event === 'assessment_started') {
      callbacks?.onAssessmentStarted?.(chunk as KomplyanceAiAssessmentStartedChunk);
    } else if (chunk.event === 'assessment_progress') {
      callbacks?.onAssessmentProgress?.(chunk as KomplyanceAiAssessmentProgressChunk);
    } else if (chunk.event === 'assessment_completed') {
      callbacks?.onAssessmentCompleted?.(chunk as KomplyanceAiAssessmentCompletedChunk);
    } else if (chunk.event === 'step_progress') {
      callbacks?.onStepProgress?.(chunk as KomplyanceAiStepProgressChunk);
    } else {
      console.warn('Unknown chunk event:', chunk);
    }

    return false;
  } catch (parseError) {
    console.error('Failed to process chunk:', chunk);
    console.warn('Failed to parse streaming data:', parseError);

    return false;
  }
};

const processStreamResponse = async (
  response: Response,
  controller: AbortController,
  callbacks?: StreamCallbacks,
): Promise<StreamController> => {
  if (!response.body) throw new Error('No response body available');

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  // Held on an object rather than in plain locals: they are written inside
  // `handleEventPart` below, and TypeScript's flow analysis cannot see a write
  // made through a closure — it would narrow the locals to their initial
  // `null`/`false` at the read sites after the loop.
  const seen: { lastChunk: KomplyanceAiChunk | null; any: boolean } = {
    lastChunk: null,
    any: false,
  };
  let completedGracefully = false;
  // Carries the tail of a read that ended mid-event. A network read boundary
  // falls wherever TCP decides, not on our delimiter, so a JSON event is
  // regularly split across two reads. Parsing each read in isolation made both
  // halves unparseable and the `catch` below dropped them with only a warning —
  // silently losing a slice of the answer. For a markdown table that is often a
  // row or the `| :--- |` separator, which is why tables intermittently
  // rendered as plain text.
  let buffer = '';

  /**
   * Parse and dispatch one complete delimited event. Returns true when the
   * event was the end-of-stream signal. Shared by the read loop and the
   * end-of-stream flush so both treat an event identically.
   */
  const handleEventPart = (chunkPart: string): boolean => {
    const trimmed = chunkPart.trim();
    // Skip empty chunks and SSE comment lines (e.g., ": ok")
    if (trimmed === '' || trimmed.startsWith(':')) return false;

    try {
      const data = KomplyanceAiChunkSchema.parse(JSON.parse(trimmed));
      seen.lastChunk = data;
      seen.any = true;
      return processkomplyanceAiChunk(data, callbacks);
    } catch (error) {
      console.warn('Failed to parse streaming chunk:', error);
      return false;
    }
  };

  try {
    try {
      while (true) {
        if (controller.signal.aborted) {
          reader.cancel();
          break;
        }

        const { done, value } = await reader.read();
        if (done) {
          // A stream can end without a trailing delimiter, leaving its last
          // event in the buffer. Flush it rather than dropping it.
          buffer += decoder.decode();
          if (handleEventPart(buffer)) completedGracefully = true;
          buffer = '';
          break;
        }

        buffer += decoder.decode(value, { stream: true });

        // The NestJS proxy strips "data: " and delimits each JSON event.
        // Anything this read left incomplete stays in `buffer` for the next one.
        const { events, rest } = takeCompleteEvents(buffer);
        buffer = rest;

        let streamEnded = false;
        for (const chunkPart of events) {
          if (handleEventPart(chunkPart)) {
            streamEnded = true;
            break;
          }
        }

        if (streamEnded) {
          completedGracefully = true;
          return {
            abortController: controller,
            abort: () => controller.abort(),
          };
        }
      }
    } catch (readError) {
      // Mid-stream read failure (network drop, server hang-up, etc.).
      // Swallow it — the silent-finalize block below will persist whatever
      // we already streamed so the user keeps their message instead of
      // seeing an alert that wipes it.
      console.warn('Stream read failed mid-flow:', readError);
    }
  } finally {
    try { reader.releaseLock(); } catch { /* reader may already be released */ }
  }

  // Stream ended without workflow_finished (natural EOF or read error).
  // Synthesize a completion using the last chunk's ids so the UI clears its
  // loading state and persists the accumulated buffer as a finalized message
  // — instead of firing an error alert that removes the message entirely.
  if (!completedGracefully && seen.any && seen.lastChunk && !controller.signal.aborted) {
    const { lastChunk } = seen;
    callbacks?.onComplete?.({
      event: 'workflow_finished',
      conversation_id: lastChunk.conversation_id,
      message_id: lastChunk.message_id,
      created_at: lastChunk.created_at,
      task_id: lastChunk.task_id,
      data: { status: 'succeeded', outputs: { answer: '' } },
    });
  }

  return {
    abortController: controller,
    abort: () => controller.abort(),
  };
};

export const streamRequest = async (
  // url: string,
  body: unknown,
  callbacks?: StreamCallbacks,
  abortController?: AbortController,
  options?: StreamRequestOptions,
): Promise<StreamController> => {
  const authStore = await container.getAsync<AuthStore>(DI_TOKENS.AuthStore);
  const token = authStore.token;
  const baseURL = apiClient.defaults.baseURL || '';

  // Create abort controller if not provided
  const controller = abortController || new AbortController();

  const endpoint = resolveStreamEndpoint(options);

  try {
    const response = await fetch(`${baseURL}/${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify(body),
      signal: controller.signal,
    });

    if (response.status === 429) {
      // Rate limit exceeded — extract Retry-After and notify via error callback
      const retryAfter = response.headers.get('Retry-After');
      let errorMessage = 'Rate limit exceeded';
      try {
        const data = await response.json();
        errorMessage = data.message || data.detail || errorMessage;
      } catch { /* ignore parse errors */ }

      callbacks?.onError?.({
        event: 'error',
        err: errorMessage,
        code: 'komplyance_AI.RATE_LIMIT_EXCEEDED',
        retryAfter: retryAfter ? parseInt(retryAfter, 10) : 30,
        conversation_id: '',
        message_id: '',
        created_at: 0,
        task_id: '',
      });
      return { abortController: controller, abort: () => controller.abort() };
    }

    if (response.status === 401) {
      // Token expired — attempt refresh and retry once
      const newToken = await refreshAccessTokenForStream(authStore);
      if (newToken) {
        const retryResponse = await fetch(`${baseURL}/${endpoint}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${newToken}`,
          },
          body: JSON.stringify(body),
          signal: controller.signal,
        });

        if (!retryResponse.ok) {
          const error = await parseStreamError(retryResponse);
          throw new Error(error);
        }

        return processStreamResponse(retryResponse, controller, callbacks);
      }

      const error = await parseStreamError(response);
      throw new Error(error);
    }

    if (!response.ok) {
      const error = await parseStreamError(response);
      throw new Error(error);
    }

    return await processStreamResponse(response, controller, callbacks);
  } catch (error) {
    const err = error instanceof Error ? error : new Error(String(error));
    console.error('Failed to stream request:', err);

    // Suppress benign abort-triggered errors (user clicked Stop) — those are
    // not real failures and the UI handles them via the stop-generation flow.
    const isAbort = err.name === 'AbortError' || controller.signal.aborted;

    if (!isAbort) {
      // Surface fetch/network/parse failures through the same error channel
      // that backend-emitted `error` events use so the UI can show the retry
      // button. Without this, a 500/timeout/network error would silently leave
      // the stream spinning forever.
      callbacks?.onError?.({
        event: 'error',
        err: err.message || 'Stream request failed',
        recoverable: true,
        conversation_id: '',
        message_id: '',
        created_at: 0,
        task_id: '',
      });
    }

    return {
      abortController: controller,
      abort: () => controller.abort(),
    };
  }
};

// API Service
export class komplyanceAiApiService {
  static async sendMessage(
    messageData: unknown,
    onMetadata?: (chunk: KomplyanceAiWorkflowStartedChunk) => void,
  ): Promise<void> {
    const streamingStore = await container.getAsync<StreamingStore>(DI_TOKENS.StreamingStore);
    const rawData = (messageData ?? {}) as { conversationId?: string; deepResearch?: boolean };
    let conversationId: string | null = rawData.conversationId ?? null;

    // `deepResearch` is a frontend-only routing hint that tells us which upstream
    // endpoint to hit; it is not part of the NestJS `SendMessageDto` contract,
    // so we strip it out before forwarding the payload.
    const { deepResearch, ...requestBody } = rawData;

    // Create abort controller before starting the stream
    const abortController = new AbortController();

    // Create the stream controller wrapper immediately so it's available in callbacks
    const streamController = {
      abortController,
      abort: () => abortController.abort(),
    };

    // Start the stream
    await streamRequest(
      requestBody,
      {
        onMetadata: (chunk) => {
          conversationId = chunk.conversation_id;
          // Store the active stream in the store
          streamingStore.setActiveStream(conversationId, chunk.task_id, streamController);
          // Call the temporary onMetadata callback if provided (called once, immediately)
          onMetadata?.(chunk);
          // Notify all listeners subscribed to this conversation ID
          streamingStore.notifyMetadataListeners(conversationId, chunk);
        },
        onError: (chunk) => {
          const id = conversationId || chunk.conversation_id;
          if (id) {
            // Mark stream as inactive on error and clear accumulated content
            streamingStore.setStreamActive(id, false);
            streamingStore.clearStreamingContent(id);
            streamingStore.notifyErrorListeners(id, chunk);
          }
        },
        onComplete: (chunk) => {
          const id = conversationId || chunk.conversation_id;
          if (id) {
            // Notify listeners first so they can read the final accumulated content,
            // then clear so stale content doesn't re-appear on component remount.
            streamingStore.notifyCompleteListeners(id, chunk);
            streamingStore.setStreamActive(id, false);
            streamingStore.clearStreamingContent(id);
          }
        },
        onChunk: (chunk) => {
          const id = conversationId || chunk.conversation_id;
          if (id) {
            // Accumulate the message content in the store
            streamingStore.appendStreamingMessage(id, chunk.answer);
            streamingStore.notifyChunkListeners(id, chunk);
          }
        },
        onDocuments: (chunk) => {
          const id = conversationId || chunk.conversation_id;
          if (id) {
            // Accumulate the references in the store
            const references = chunk.metadata?.retriever_resources || [];
            streamingStore.setStreamingReferences(id, references);
            streamingStore.notifyDocumentsListeners(id, chunk);
          }
        },
        onFileIngestProgress: (chunk) => {
          const id = conversationId || chunk.conversation_id;
          if (id) {
            streamingStore.setStreamingStage(id, chunk.data.stage, chunk.data.message);
            streamingStore.notifyFileIngestProgressListeners(id, chunk);
          }
        },
        onAssessmentStarted: (chunk) => {
          const id = conversationId || chunk.conversation_id;
          if (id) {
            streamingStore.setStreamingStage(id, 'assessment_started', chunk.data.file_name);
            streamingStore.notifyAssessmentStartedListeners(id, chunk);
          }
        },
        onAssessmentProgress: (chunk) => {
          const id = conversationId || chunk.conversation_id;
          if (id) {
            streamingStore.setStreamingStage(id, chunk.data.stage, chunk.data.message);
            streamingStore.notifyAssessmentProgressListeners(id, chunk);
          }
        },
        onAssessmentCompleted: (chunk) => {
          const id = conversationId || chunk.conversation_id;
          if (id) {
            streamingStore.setStreamingStage(id, 'assessment_completed', '');
            streamingStore.notifyAssessmentCompletedListeners(id, chunk);
          }
        },
        onStepProgress: (chunk) => {
          const id = conversationId || chunk.conversation_id;
          if (id) {
            streamingStore.setStreamingStage(id, chunk.data.stage, chunk.data.message);
            streamingStore.notifyStepProgressListeners(id, chunk);
          }
        },
      },
      abortController,
      { deepResearch: Boolean(deepResearch) },
    );
  }

  static async stopGeneration(taskId: string): Promise<{
    result: StopGenerationResponse;
  }> {
    try {
      const response = await apiClient.post<
        { result: StopGenerationResponse }
      >(API_ENDPOINTS.STOP_GENERATION(taskId));

      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  static async sendFeedback(
    messageId: string,
    rating: FeedbackRating,
  ): Promise<{ result: string }> {
    try {
      const response = await apiClient.post<{ result: string }>(
        API_ENDPOINTS.SEND_FEEDBACK(messageId),
        { rating },
      );

      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Abort a stream locally and, when we know its task id, tell the server to
   * stop generating too.
   *
   * `taskId` is optional because it only arrives with the first chunk — a
   * stream aborted before then can still be torn down locally, and callers
   * shouldn't have to hand-roll that half themselves.
   */
  static async abortStream(conversationId: string, taskId?: string): Promise<void> {
    const streamingStore = await container.getAsync<StreamingStore>(DI_TOKENS.StreamingStore);
    const stream = streamingStore.getStream(conversationId);

    if (stream && stream.isActive) {
      const controller = streamingStore.getAbortController(conversationId);
      if (controller) {
        controller.abort();
      }
      streamingStore.setStreamActive(conversationId, false);
      // Don't clear accumulated content on abort - let the component handle it
      // The component may want to keep the partial message
      if (taskId) {
        await komplyanceAiApiService.stopGeneration(taskId);
      }
    }
  }
}

export const useSendFeedback = () => {
  return useMutation<{ result: string }, Error, { messageId: string; rating: FeedbackRating }>({
    mutationKey: ['send-feedback'],
    mutationFn: ({ messageId, rating }) => komplyanceAiApiService.sendFeedback(messageId, rating),
  });
};
